/**
 * Generic CSV writer that converts an array of objects into a CSV string.
 * - If saveHeaderRow is true (default), the first line contains the keys of the first row.
 * - Subsequent lines contain the values in the same key order.
 * - Values are CSV-escaped per RFC 4180 basics (quotes doubled, fields wrapped in quotes when needed).
 */
export function rowsToCSVString<T extends object>(
  rows: T[],
  saveHeaderRow: boolean = true
): string {
  if (!rows || rows.length === 0) return "";

  // Try to infer headers from the object's own enumerable properties first
  const first: unknown = rows[0];
  let headers: string[] =
    first && typeof first === "object"
      ? Object.keys(first as Record<string, unknown>)
      : [];

  // Fallback: if headers cannot be inferred (e.g., class instances with non-enumerable fields),
  // stringify and parse to get plain objects we can read keys from
  let dataSource: Array<Record<string, unknown>> = rows as Array<
    Record<string, unknown>
  >;
  if (headers.length === 0) {
    try {
      const jsonRows = JSON.parse(JSON.stringify(rows)) as Array<
        Record<string, unknown>
      >;
      if (
        Array.isArray(jsonRows) &&
        jsonRows.length > 0 &&
        typeof jsonRows[0] === "object" &&
        jsonRows[0] !== null
      ) {
        headers = Object.keys(jsonRows[0]);
        dataSource = jsonRows;
      }
    } catch {
      // If stringify fails, proceed with empty headers which will yield an empty CSV
    }
  }

  if (headers.length === 0 && saveHeaderRow) {
    return "";
  }

  const escapeCSV = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    // Convert objects (incl. arrays, dates) to string safely
    let str = typeof value === "string" ? value : String(value);
    const needsQuotes = /[",\n\r]/.test(str) || /^\s|\s$/.test(str);
    // Escape double quotes by doubling them
    if (str.includes('"')) {
      str = str.replace(/"/g, '""');
    }
    return needsQuotes ? `"${str}"` : str;
  };

  const lines: string[] = [];
  if (saveHeaderRow) {
    lines.push(headers.map((h) => escapeCSV(h)).join(","));
  }

  for (const row of dataSource) {
    const line = headers.map((h) => escapeCSV(row[h])).join(",");
    lines.push(line);
  }

  return lines.join("\n");
}
